import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from '@telegram-apps/telegram-ui';
import { backButton, onBackButtonClick } from '@telegram-apps/sdk';
import { useRequestHistory } from '../hooks/useRequests';
import { hapticFeedback } from '../lib/telegram';
import type { HistoryEntry, HistoryChange } from '../lib/api';
import './HistoryPage.css';

// Action configuration with semantic colors
const ACTION_CONFIG: Record<
  string,
  { icon: string; label: string; color: string; bgGradient: string }
> = {
  create: {
    icon: '✦',
    label: 'Создано',
    color: '#34C759',
    bgGradient: 'linear-gradient(135deg, rgba(52, 199, 89, 0.15) 0%, rgba(52, 199, 89, 0.05) 100%)',
  },
  update: {
    icon: '↻',
    label: 'Изменено',
    color: '#FF9F0A',
    bgGradient: 'linear-gradient(135deg, rgba(255, 159, 10, 0.15) 0%, rgba(255, 159, 10, 0.05) 100%)',
  },
  info: {
    icon: '◉',
    label: 'Информация',
    color: '#64D2FF',
    bgGradient: 'linear-gradient(135deg, rgba(100, 210, 255, 0.15) 0%, rgba(100, 210, 255, 0.05) 100%)',
  },
};

function formatDateTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  } catch {
    return dateString;
  }
}

function formatRelativeTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays === 1) return 'вчера';
    if (diffDays < 7) return `${diffDays} дн назад`;
    return formatDateTime(dateString);
  } catch {
    return dateString;
  }
}

// Single change display component
function ChangeItem({ change, index }: { change: HistoryChange; index: number }) {
  const hasOldValue = change.oldValue !== null;
  const hasNewValue = change.newValue !== null;

  return (
    <div
      className="history-change-card"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <span className="history-change-field">{change.label}</span>
      <div className="history-change-content">
        {hasOldValue && hasNewValue ? (
          <>
            <span className="history-value-old">{change.oldValue}</span>
            <span className="history-value-arrow">→</span>
            <span className="history-value-new">{change.newValue}</span>
          </>
        ) : hasNewValue ? (
          <span className="history-value-new">{change.newValue}</span>
        ) : (
          <span className="history-value-removed">удалено</span>
        )}
      </div>
    </div>
  );
}

// Timeline entry component
function TimelineItem({
  entry,
  index,
  isLast,
}: {
  entry: HistoryEntry;
  index: number;
  isLast: boolean;
}) {
  const [expanded, setExpanded] = useState(index === 0);
  const config = ACTION_CONFIG[entry.action] || ACTION_CONFIG.info;
  const hasChanges = entry.changes.length > 0;

  const handleToggle = () => {
    if (hasChanges) {
      hapticFeedback('light');
      setExpanded(!expanded);
    }
  };

  return (
    <div
      className={`timeline-item ${expanded ? 'timeline-item--expanded' : ''}`}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      {/* Timeline connector */}
      <div className="timeline-connector">
        <div
          className="timeline-dot"
          style={{
            background: config.bgGradient,
            borderColor: config.color,
          }}
        >
          <span style={{ color: config.color }}>{config.icon}</span>
        </div>
        {!isLast && <div className="timeline-line" />}
      </div>

      {/* Content */}
      <div className="timeline-content">
        <div
          className={`timeline-card ${hasChanges ? 'timeline-card--clickable' : ''}`}
          onClick={handleToggle}
          style={{ background: config.bgGradient }}
        >
          <div className="timeline-header">
            <div className="timeline-title">
              <span className="timeline-action" style={{ color: config.color }}>
                {config.label}
              </span>
              {hasChanges && (
                <span className="timeline-badge">{entry.changes.length}</span>
              )}
            </div>
            {hasChanges && (
              <span className={`timeline-chevron ${expanded ? 'timeline-chevron--open' : ''}`}>
                ›
              </span>
            )}
          </div>

          <div className="timeline-meta">
            <span className="timeline-time">{formatRelativeTime(entry.createdAt)}</span>
            {entry.createdBy && (
              <>
                <span className="timeline-separator">•</span>
                <span className="timeline-author">{entry.createdBy}</span>
              </>
            )}
          </div>

          {/* Expandable changes */}
          {hasChanges && (
            <div className={`timeline-changes ${expanded ? 'timeline-changes--visible' : ''}`}>
              <div className="timeline-changes-inner">
                {entry.changes.map((change, idx) => (
                  <ChangeItem key={idx} change={change} index={expanded ? idx : 0} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Cards view entry component
function CardEntry({
  entry,
  index,
}: {
  entry: HistoryEntry;
  index: number;
}) {
  const [expanded, setExpanded] = useState(index === 0);
  const config = ACTION_CONFIG[entry.action] || ACTION_CONFIG.info;
  const hasChanges = entry.changes.length > 0;

  const handleToggle = () => {
    if (hasChanges) {
      hapticFeedback('light');
      setExpanded(!expanded);
    }
  };

  return (
    <div
      className={`card-entry ${expanded ? 'card-entry--expanded' : ''}`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div
        className={`card-entry-main ${hasChanges ? 'card-entry-main--clickable' : ''}`}
        onClick={handleToggle}
      >
        <div
          className="card-entry-icon"
          style={{
            background: config.bgGradient,
            color: config.color,
          }}
        >
          {config.icon}
        </div>
        <div className="card-entry-body">
          <div className="card-entry-top">
            <span className="card-entry-action" style={{ color: config.color }}>
              {config.label}
            </span>
            {hasChanges && (
              <span className="card-entry-count">{entry.changes.length} изм.</span>
            )}
            {hasChanges && (
              <span className={`card-entry-chevron ${expanded ? 'card-entry-chevron--open' : ''}`}>
                ›
              </span>
            )}
          </div>
          <div className="card-entry-meta">
            {formatRelativeTime(entry.createdAt)}
            {entry.createdBy && ` • ${entry.createdBy}`}
          </div>
        </div>
      </div>

      {/* Expandable changes */}
      {hasChanges && (
        <div className={`card-entry-changes ${expanded ? 'card-entry-changes--visible' : ''}`}>
          <div className="card-entry-changes-inner">
            {entry.changes.map((change, idx) => (
              <ChangeItem key={idx} change={change} index={expanded ? idx : 0} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function HistoryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const requestId = parseInt(id || '0', 10);
  const [viewMode, setViewMode] = useState<'cards' | 'timeline'>('timeline');

  const { data: history, isLoading, error } = useRequestHistory(requestId);

  // Setup back button
  useEffect(() => {
    if (backButton.show.isAvailable()) {
      backButton.show();
    }

    let removeListener: (() => void) | undefined;
    if (onBackButtonClick.isAvailable()) {
      removeListener = onBackButtonClick(() => {
        hapticFeedback('light');
        navigate(-1);
      });
    }

    return () => {
      if (backButton.hide.isAvailable()) {
        backButton.hide();
      }
      if (removeListener) {
        removeListener();
      }
    };
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="history-loading">
        <div className="history-loading-spinner">
          <Spinner size="l" />
        </div>
        <span className="history-loading-text">Загрузка истории...</span>
      </div>
    );
  }

  if (error || !history) {
    return (
      <div className="history-error">
        <div className="history-error-icon">⚠</div>
        <h2 className="history-error-title">Не удалось загрузить</h2>
        <p className="history-error-text">Проверьте подключение и попробуйте снова</p>
        <button className="history-error-button" onClick={() => navigate(-1)}>
          Вернуться назад
        </button>
      </div>
    );
  }

  return (
    <div className="history-page">
      {/* Header */}
      <header className="history-header">
        <div className="history-header-top">
          <span className="history-request-number">#{history.requestId}</span>
          <span className="history-status-badge">{history.currentStatus}</span>
        </div>
        <h1 className="history-subject">{history.subject}</h1>
        <div className="history-entries-count">
          {history.entries.length} {getEntriesWord(history.entries.length)}
        </div>
      </header>

      {/* View mode toggle */}
      <div className="history-toggle-container">
        <div className="history-toggle">
          <button
            className={`history-toggle-btn ${viewMode === 'cards' ? 'history-toggle-btn--active' : ''}`}
            onClick={() => {
              hapticFeedback('light');
              setViewMode('cards');
            }}
          >
            <span className="history-toggle-icon">☰</span>
            Карточки
          </button>
          <button
            className={`history-toggle-btn ${viewMode === 'timeline' ? 'history-toggle-btn--active' : ''}`}
            onClick={() => {
              hapticFeedback('light');
              setViewMode('timeline');
            }}
          >
            <span className="history-toggle-icon">⏱</span>
            Таймлайн
          </button>
          <div
            className="history-toggle-indicator"
            style={{
              transform: `translateX(${viewMode === 'timeline' ? '100%' : '0'})`,
            }}
          />
        </div>
      </div>

      {/* Content */}
      {history.entries.length === 0 ? (
        <div className="history-empty">
          <div className="history-empty-icon">📭</div>
          <p className="history-empty-text">История изменений пуста</p>
        </div>
      ) : viewMode === 'timeline' ? (
        <div className="history-timeline">
          {history.entries.map((entry, index) => (
            <TimelineItem
              key={entry.id}
              entry={entry}
              index={index}
              isLast={index === history.entries.length - 1}
            />
          ))}
        </div>
      ) : (
        <div className="history-cards">
          {history.entries.map((entry, index) => (
            <CardEntry key={entry.id} entry={entry} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}

// Helper function for correct Russian word form
function getEntriesWord(count: number): string {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'записей';
  }
  if (lastDigit === 1) {
    return 'запись';
  }
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'записи';
  }
  return 'записей';
}
